import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeriesTypeCategoryFields } from "components/series-type-category-fields/series-type-category-fields";
import {
  updateAssetsResponsibleSettings,
  updateOnboardingDocumentSettings,
} from "services/company.service";
import {
  ONBOARDING_DOCUMENT_SLOTS,
  ONBOARDING_UPLOAD_TYPES,
  OnboardingDocumentSettings,
  OnboardingDocumentSlotConfig,
} from "types/OnboardingDocumentSettings";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";
import { useCategoryPaths } from "../use-category-paths";
import { Role, RoleRow } from "./ApprovalRolesTab";

// Asset-request e-mails go only to this person; with nobody chosen they go to everyone
// holding the "Assets" permission, so unlike the signing roles an empty row is not a skipped step.
const ASSETS_RESPONSIBLE_ROLE: Role = {
  field: "assetsResponsibleUser",
  labelKey: "settings_roles_assets_responsible",
  hintKey: "settings_roles_assets_responsible_hint",
  pickerLabelKey: "select_user",
  save: updateAssetsResponsibleSettings,
  onlyActivated: false,
  clearLabelKey: "settings_roles_assets_responsible_clear",
  emptyHintKey: "settings_roles_assets_responsible_empty",
};

interface DocumentRowProps {
  label: string;
  /** Uploaded documents only have a category; the other two stay empty. */
  saved: OnboardingDocumentSlotConfig;
  /** Generated documents also choose the series and the document type. */
  hasSeriesAndType: boolean;
  categoryPath: (categoryId: string) => string;
  onSave: (draft: OnboardingDocumentSlotConfig) => Promise<unknown>;
}

function DocumentRow({
  label,
  saved,
  hasSeriesAndType,
  categoryPath,
  onSave,
}: DocumentRowProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(saved);
  const { save, saving, error } = useSaveWithToast();

  const submit = async () => {
    if (await save(() => onSave(edit.draft))) edit.cancel();
  };

  return (
    <SettingRow
      label={label}
      value={
        saved.category ? (
          t("onboarding_filed_in", { path: categoryPath(saved.category) })
        ) : (
          <span className="text-amber-800">
            {t("onboarding_category_missing")}
          </span>
        )
      }
      action={<ChangeLink onClick={edit.start} />}
    >
      {edit.editing && (
        <EditPanel
          onSave={submit}
          onCancel={edit.cancel}
          saving={saving}
          error={error}
        >
          <SeriesTypeCategoryFields
            seriesId={edit.draft.series}
            fileTypeId={edit.draft.fileType}
            categoryId={edit.draft.category}
            onSeriesChange={(series) =>
              edit.setDraft({ ...edit.draft, series })
            }
            onFileTypeChange={(fileType) =>
              edit.setDraft({ ...edit.draft, fileType })
            }
            onCategoryChange={(category) =>
              edit.setDraft({ ...edit.draft, category })
            }
            hideSeriesAndType={!hasSeriesAndType}
          />
        </EditPanel>
      )}
    </SettingRow>
  );
}

interface OnboardingTabProps {
  onboardingDocumentSettings: OnboardingDocumentSettings | null;
  /** Id of the person who gets the asset-request e-mails, empty when nobody is set. */
  assetsResponsibleUser: string | null;
  /** Reloads the company so the rows show what was just saved. */
  onRefresh: () => Promise<unknown>;
}

/** Where each document made or collected while hiring is filed in the document tree. */
export default function OnboardingTab({
  onboardingDocumentSettings,
  assetsResponsibleUser,
  onRefresh,
}: OnboardingTabProps) {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const categoryPath = useCategoryPaths(companyId);

  // The endpoint changes only the slots it is sent, so a row sends just itself.
  const saveDocument = async (
    slotKey: string,
    config: OnboardingDocumentSlotConfig
  ) => {
    await updateOnboardingDocumentSettings(
      { documents: { [slotKey]: config } },
      companyId
    );
    await onRefresh();
  };

  const saveUpload = async (fileType: string, categoryId: string) => {
    await updateOnboardingDocumentSettings(
      { uploads: { [fileType]: categoryId } },
      companyId
    );
    await onRefresh();
  };

  return (
    <div className="space-y-5">
      <StatusBanner
        tone="info"
        title={t("onboarding_document_settings_hint")}
      />

      <SettingsCard
        title={t("onboarding_generated_documents")}
        description={t("onboarding_generated_documents_hint")}
      >
        {ONBOARDING_DOCUMENT_SLOTS.map(({ slotKey, labelKey }) => {
          const stored = onboardingDocumentSettings?.documents?.[slotKey];
          return (
            <DocumentRow
              key={slotKey}
              label={t(labelKey)}
              saved={{
                series: stored?.series || "",
                fileType: stored?.fileType || "",
                category: stored?.category || "",
              }}
              hasSeriesAndType
              categoryPath={categoryPath}
              onSave={(draft) => saveDocument(slotKey, draft)}
            />
          );
        })}
      </SettingsCard>

      <SettingsCard
        title={t("onboarding_uploaded_documents")}
        description={t("onboarding_uploaded_documents_hint")}
      >
        {ONBOARDING_UPLOAD_TYPES.map(({ fileType, labelKey }) => (
          <DocumentRow
            key={fileType}
            label={t(labelKey)}
            saved={{
              series: "",
              fileType: "",
              category: onboardingDocumentSettings?.uploads?.[fileType] || "",
            }}
            hasSeriesAndType={false}
            categoryPath={categoryPath}
            onSave={(draft) => saveUpload(fileType, draft.category)}
          />
        ))}
      </SettingsCard>

      <SettingsCard>
        <RoleRow
          role={ASSETS_RESPONSIBLE_ROLE}
          companyId={companyId!}
          holderId={assetsResponsibleUser || ""}
          onRefresh={onRefresh}
        />
        <Link
          to={`/app/${companyId}/settings/permissions`}
          className="mt-3 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800 max-md:min-h-11 [@media(pointer:coarse)]:min-h-11"
        >
          {t("onboarding_asset_requests_link")}
        </Link>
      </SettingsCard>
    </div>
  );
}
